import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "wouter";
import {
  useGetDailyChecklist,
  useListTaskTemplates,
  useListUsers,
  getListUsersQueryKey,
  useMarkTaskChecklistItemDone,
  useMarkTaskChecklistItemNa,
  useValidateTaskChecklistItem,
  getGetDailyChecklistQueryKey,
} from "@workspace/api-client-react";
import type { DailyChecklistEntry, DailyChecklistItem, User } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ListChecks, MoreVertical, ExternalLink, ChevronRight } from "lucide-react";
import { ProofCaptureDialog, type ProofPayload } from "./proof-capture-dialog";
import { RejectDialog } from "./reject-dialog";

// Phase 43 Plan 06 (TREC-03/TREC-10, 43-RESEARCH Pattern 11) — the operator's
// mockup screen 1 (.planning/spec/task-ui-mockups.md). Phase 44 Plan 08
// (TCHK-04) made this tab interactive: Done = 1 tap on the checkbox
// (useMarkTaskChecklistItemDone), N/A = a secondary ⋮ menu that opens a
// required-note dialog (useMarkTaskChecklistItemNa — server 422s under 10
// chars), Validate = a distinct 1-tap affordance gated on the server's
// `canIValidate` hint (44-07) — see ChecklistItemRow below.
//
// Phase 49 Plan 08 (TASN-02/TCHN-01/02/03) — this is the EXTRACTED body of
// the original daily-checklist.tsx (now a thin re-export, see that file),
// parameterized by `trigger` ('daily'|'weekly'|'monthly') so the Daily/
// Weekly/Monthly tabs of the new tabbed Checklist page all reuse the
// IDENTICAL period read path + the IDENTICAL ChecklistItemRow two-step —
// zero regression, one live copy of this logic. `ChecklistItemRow` (and
// the resolveAssignee/AssigneeBadge helpers it uses) are also imported by
// checklist-event-tab.tsx, so the Onboarding/Exit event tabs get the exact
// same done/N/A/validate affordances and person-vs-role avatar rendering
// without a second copy of this logic living anywhere.
//
// Consumes ONLY generated hooks: useGetDailyChecklist (43-05's grouped read
// feed, server-scoped to trigger IN (daily,weekly,monthly) per 49-02 —
// task_checklist_item COPIES, never a live join to task_template_item, so a
// same-day template edit never mutates what's rendered here — TREC-07) +
// useListTaskTemplates (freq/byweekday/nth/byMonthDay for the "Resets
// every…" subtitle, PLUS `trigger` for this tab's own client-side partition
// of the shared feed) + useListUsers (role-holder / pinned-person avatar +
// doer/validator name resolution).
//
// useListUsers() is admin-only server-side (users.ts requireAdmin) — the
// exact staff who work these rounds (front_desk/housekeeping/ops_lead) will
// see it error out. Mirrors task-create.tsx's `usersUnavailable` graceful
// degradation: an unavailable roster falls back to a muted ROLE-NAME badge
// for the assignee slot, and a generic "a teammate"/"a supervisor" label in
// the done-by/validated-by status line (never a blank, which would wrongly
// imply nobody acted on the item).

const WEEKDAY_LABELS: Record<string, string> = {
  MO: "Monday", TU: "Tuesday", WE: "Wednesday", TH: "Thursday", FR: "Friday", SA: "Saturday", SU: "Sunday",
};

function ordinal(n: number): string {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}

/** "Resets daily" / "Resets every Monday" / a monthly equivalent — derived from
 *  the template's freq+byweekday+nth+byMonthDay (from useListTaskTemplates).
 *
 *  NOTE: no time-of-day is shown. The scheduler gates on the calendar DATE, so a
 *  fresh instance spawns shortly after IST midnight — always present before staff
 *  arrive, but not at a literal hour. `at_time` is stored for a future "due by"
 *  concept but is intentionally NOT surfaced here, so the label never claims a
 *  spawn hour the engine doesn't honor. */
function resetSubtitle(
  freq: string | undefined,
  byweekday: string | null | undefined,
  nth: number | null | undefined,
  byMonthDay: number | null | undefined,
): string {
  if (freq === "weekly") {
    const day = byweekday ? (WEEKDAY_LABELS[byweekday] ?? byweekday) : null;
    return day ? `Resets every ${day}` : "Resets weekly";
  }
  if (freq === "monthly") {
    if (byweekday && nth) return `Resets the ${ordinal(nth)} ${WEEKDAY_LABELS[byweekday] ?? byweekday} of the month`;
    if (byMonthDay) return `Resets on the ${ordinal(byMonthDay)} of the month`;
    return "Resets monthly";
  }
  return "Resets daily";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

type Assignee =
  | { kind: "user"; user: User; pinned: boolean }
  | { kind: "unassigned" }
  | { kind: "role-only" };

/** Person-OR-role resolution (Phase 49 TASN-01/02, 49-RESEARCH Pattern 7).
 * A person-pinned item (`assignedUserId != null`) resolves to that SPECIFIC
 * user first — never the role fallback, even if the pin happens to also
 * hold the role. A role-assigned item (`assignedUserId == null`) resolves
 * exactly as before: whoever currently holds `assignedRole` at the space
 * (client-side filter over useListUsers(), 1 match wins, >1 matches lowest
 * id wins — a known simplification, not a real "who's on shift" resolver).
 * `usersUnavailable` degrades BOTH paths to a muted role-name badge (a
 * non-admin viewer can't resolve any individual user, pinned or not). */
function resolveAssignee(
  item: Pick<DailyChecklistItem, "assignedRole" | "assignedUserId">,
  users: User[] | undefined,
  usersUnavailable: boolean,
): Assignee {
  if (usersUnavailable) return { kind: "role-only" };
  if (item.assignedUserId != null) {
    const pinned = (users ?? []).find((u) => u.id === item.assignedUserId);
    // Pinned user missing from the roster (deactivated/removed) degrades to
    // "unassigned", NOT the role fallback — a person-pinned item is
    // intentionally not role-resolved once pinned.
    return pinned ? { kind: "user", user: pinned, pinned: true } : { kind: "unassigned" };
  }
  const matches = (users ?? []).filter((u) => u.role === item.assignedRole && u.active).sort((a, b) => a.id - b.id);
  if (matches.length === 0) return { kind: "unassigned" };
  return { kind: "user", user: matches[0], pinned: false };
}

function AssigneeBadge({ item, users, usersUnavailable }: {
  item: Pick<DailyChecklistItem, "assignedRole" | "assignedUserId">;
  users: User[] | undefined;
  usersUnavailable: boolean;
}) {
  const assignee = resolveAssignee(item, users, usersUnavailable);
  if (assignee.kind === "user") {
    return (
      <Avatar className="h-7 w-7" title={assignee.pinned ? `${assignee.user.name} (assigned)` : assignee.user.name}>
        <AvatarFallback className="text-[10px] font-medium">{initials(assignee.user.name)}</AvatarFallback>
      </Avatar>
    );
  }
  if (assignee.kind === "role-only") {
    return <Badge variant="outline" className="text-[10px] capitalize font-normal">{item.assignedRole.replace(/_/g, " ")}</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">Unassigned</Badge>;
}

/** Resolves a user id to a display name for the done-by/validated-by status
 * line. Falls back to `fallback` (e.g. "a teammate") when the roster is
 * unavailable (non-admin viewer, usersUnavailable) rather than a blank —
 * mirrors AssigneeBadge's graceful degradation. */
function resolveUserName(
  id: number | null | undefined,
  users: User[] | undefined,
  usersUnavailable: boolean,
  fallback: string,
): string | null {
  if (id == null) return null;
  if (usersUnavailable) return fallback;
  return users?.find((u) => u.id === id)?.name ?? `User #${id}`;
}

function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}
/** Surfaces the server's message/error verbatim (mirrors routing-settings.tsx's errStatus/errData convention). */
function errMessage(err: unknown, fallback: string): string {
  const data = errData(err);
  return (data?.message as string) ?? (data?.error as string) ?? fallback;
}

/** Phase 42 Plan 14 (TPRF-01/02/03) — renders the proof attached to an
 * already-ticked item: photo thumbnails (each a link out to the full-size
 * Supabase Storage URL), the note verbatim, or the signature image + signer
 * name + server-stamped signedAt. This is exactly what the validator reviews
 * to decide validate-vs-reject, and the reason the evidence exists at all.
 * Renders nothing for a proof-free item (no fields ever populate). */
function ProofSummary({ item }: { item: DailyChecklistItem }) {
  const photoUrls = item.proofUrls ?? [];
  const hasPhotos = photoUrls.length > 0;
  const hasNote = (item.proofNote ?? "").trim().length > 0;
  const hasSignature = item.signatureUrl != null;
  if (!hasPhotos && !hasNote && !hasSignature) return null;
  return (
    <div className="mt-1.5 space-y-1.5">
      {hasPhotos && (
        <div className="flex flex-wrap gap-1.5">
          {photoUrls.map((url, i) => (
            <a
              key={`${url}-${i}`}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="block h-12 w-12 overflow-hidden rounded border"
            >
              <img src={url} alt="Attached proof" className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}
      {hasNote && <p className="text-xs italic text-muted-foreground">&ldquo;{item.proofNote}&rdquo;</p>}
      {hasSignature && (
        <div className="flex items-center gap-2">
          <img src={item.signatureUrl!} alt="Client signature" className="h-8 w-auto rounded border bg-white" />
          <span className="text-[11px] text-muted-foreground">
            Signed by {item.signerName ?? "the client"}
            {item.signedAt ? ` · ${new Date(item.signedAt).toLocaleString()}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

/** Per-item done/N/A/validated status line, shown once the item has left the
 * "undone" state. Not rendered for a plain undone item (checkbox + zone
 * label speak for themselves there). Validated renders identically whether
 * a supervisor validated or the item auto-validated for a terminal doer
 * (44-RESEARCH Open Question 2 — no separate badge for the two cases). */
function ItemStatusLine({ item, users, usersUnavailable }: { item: DailyChecklistItem; users: User[] | undefined; usersUnavailable: boolean }) {
  const isDone = item.doneAt != null;
  const isNa = item.naReason != null;
  if (!isDone && !isNa) return null;
  const isValidated = item.validatedAt != null;
  const doerName = resolveUserName(item.doneByUserId, users, usersUnavailable, "a teammate");
  const validatorName = resolveUserName(item.validatedByUserId, users, usersUnavailable, "a supervisor");
  return (
    <p className="mt-1 text-[11px] text-muted-foreground">
      {isNa ? (
        <>N/A{doerName ? ` by ${doerName}` : ""}: &ldquo;{item.naReason}&rdquo;</>
      ) : (
        <>Done{doerName ? ` by ${doerName}` : ""}</>
      )}
      {isValidated ? (
        <span className="text-emerald-600 dark:text-emerald-400"> · Validated{validatorName ? ` by ${validatorName}` : ""}</span>
      ) : (
        <span> · Awaiting validation</span>
      )}
    </p>
  );
}

/** Phase 42 Plan 15 (TOCC-01, TSLA-07/08) — the occurrence-time badge plus
 * the overdue/awaiting-validation/rejected status badge. After 42-07's
 * fan-out, N occurrences of one item simply arrive as N rows — this is the
 * "flat rows with a time badge" rendering decision (Claude's Discretion,
 * locked in 42-15-PLAN.md): no new grouping component, because each
 * occurrence IS its own independently tickable, independently breachable
 * task. A collapsible header would visually re-couple exactly what the
 * fan-out decoupled.
 *
 * Renders NOTHING for a legacy item with no per-item SLA (`doerDueAt ==
 * null`) — pre-Phase-42 seeded templates must look exactly as they did
 * before this plan.
 *
 * Reads ONLY the computed-on-read booleans (`doerBreached`/
 * `validationBreached`, wired since 42-06/42-11) — never the sweep's own
 * one-time claim stamp columns, which are stale by design (TSLA-12).
 *
 * ⭐ The dual clock, made visible: the "awaiting validation" branch below is
 * reachable ONLY once the doer has already ticked (or N/A'd) the item, so a
 * stalled validator can never produce a badge on THIS row that reads as the
 * doer being late — `validationBreached` always renders as "Validation
 * overdue", worded so it is unmistakably the validator who is late. */
function OccurrenceBadges({ item }: { item: DailyChecklistItem }) {
  if (item.doerDueAt == null) return null;

  const isDone = item.doneAt != null;
  const isNa = item.naReason != null;
  const isValidated = item.validatedAt != null;
  // A rejected-and-not-yet-redone item: the doer already acted once, so the
  // actionable signal now is "redo", never "you were late" — this wins over
  // a stale overdue flag left over from before the reject (42-11 preserves
  // the doer's original window as historical fact for Phase 45, so the
  // un-ticked item can still compute as overdue here even though the doer
  // isn't who's holding things up).
  const isRejectedUnticked = item.rejectReason != null && !isDone && !isNa;

  const badges: { key: string; variant: "outline" | "secondary" | "destructive"; label: string }[] = [];

  if (item.occurrenceAtTime) {
    badges.push({ key: "time", variant: "outline", label: item.occurrenceAtTime });
  }

  if (isRejectedUnticked) {
    badges.push({ key: "status", variant: "destructive", label: "Rejected" });
  } else if ((isDone || isNa) && !isValidated) {
    badges.push(
      item.validationBreached
        ? { key: "status", variant: "destructive", label: "Validation overdue" }
        : { key: "status", variant: "secondary", label: "Awaiting validation" },
    );
  } else if (!isDone && !isNa && item.doerBreached) {
    badges.push({ key: "status", variant: "destructive", label: "Overdue" });
  }

  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {badges.map((b) => (
        <Badge key={b.key} variant={b.variant} className="shrink-0 whitespace-nowrap text-[10px] font-normal">
          {b.label}
        </Badge>
      ))}
    </div>
  );
}

/** The reject reason, shown right on the row (not only in the activity log)
 * so the doer knows exactly what to redo without hunting anywhere else.
 * Mutually exclusive with ItemStatusLine — that renders only once
 * isDone/isNa is true again; this renders only while the item sits
 * un-ticked after a reject. */
function RejectedNotice({ item, users, usersUnavailable }: {
  item: DailyChecklistItem;
  users: User[] | undefined;
  usersUnavailable: boolean;
}) {
  if (item.rejectReason == null || item.doneAt != null || item.naReason != null) return null;
  const rejectorName = resolveUserName(item.rejectedByUserId, users, usersUnavailable, "the validator");
  return (
    <p className="mt-1 text-[11px] text-destructive">
      Rejected{rejectorName ? ` by ${rejectorName}` : ""}: &ldquo;{item.rejectReason}&rdquo; — redo with fresh proof.
    </p>
  );
}

/** The done/N/A/validate two-step, shared verbatim by Daily/Weekly/Monthly
 * AND Onboarding/Exit (checklist-event-tab.tsx imports this component —
 * ONE render path for every checklist item in the app, period or event). */
export function ChecklistItemRow({ item, users, usersUnavailable, accountDeepLink }: {
  item: DailyChecklistItem;
  users: User[] | undefined;
  usersUnavailable: boolean;
  /** Phase 49 Plan 08 — set only for an exit-trigger item whose label reads
   * as the deposit-refund handoff (checklist-event-tab.tsx's heuristic).
   * Renders a "View account" nav-only deep link to the account's existing
   * Phase-36.1 Security Deposits flow; this row never moves money itself. */
  accountDeepLink?: { accountId: number };
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const doneMutation = useMarkTaskChecklistItemDone();
  const naMutation = useMarkTaskChecklistItemNa();
  const validateMutation = useValidateTaskChecklistItem();

  const [naOpen, setNaOpen] = useState(false);
  const [naNote, setNaNote] = useState("");
  // Phase 42 Plan 14 (TPRF-01/02/03) — the item's OWN copied proofType (never
  // the live template, TREC-07 discipline) gates the tick: none/null keeps
  // today's one-tap behavior byte-for-byte; photo/note/signature opens the
  // proof dialog instead of ticking immediately.
  const [proofOpen, setProofOpen] = useState(false);
  const requiresProof = item.proofType != null && item.proofType !== "none";
  // Phase 42 Plan 15 (TPRF-04) — validator reject-with-reason, shown beside
  // Validate below.
  const [rejectOpen, setRejectOpen] = useState(false);

  const isAdminRole = user?.role === "super_admin" || user?.role === "admin";
  // Person-OR-role gate (Phase 49 TASN-01, mirrors task-checklist-items.ts's
  // server-side canActOnItem EXACTLY): a person-pinned item is actionable
  // ONLY by that specific person (or the admin/ops_lead override); a
  // role-assigned item keeps the original role-match gate. A narrower
  // client gate here would let a role-matching non-pin user tap the
  // checkbox only to have the server 403 the mutation.
  const canAct = user != null && (
    isAdminRole ||
    user.role === "ops_lead" ||
    (item.assignedUserId != null ? user.id === item.assignedUserId : user.role === item.assignedRole)
  );

  const isDone = item.doneAt != null;
  const isNa = item.naReason != null;
  const isSettled = isDone || isNa;
  // Server hint (44-07, computed via canValidateItem) — already false unless
  // doneByUserId is set (done OR N/A'd), not yet validated, and not the
  // requester's own item. Deliberately NOT gated here on `item.doneAt != null`
  // — that would silently exclude every N/A'd item from ever being
  // validated (an N/A'd item has doneByUserId set but doneAt stays null),
  // which would make a round with an N/A'd required item unresolvable.
  const canValidateNow = item.canIValidate === true;

  /** Optimistic patch over the cached DailyChecklistEntry[] list — returns
   * the pre-patch snapshot so a failed mutation can roll back exactly. */
  function patchItem(patch: Partial<DailyChecklistItem>) {
    const previous = queryClient.getQueryData<DailyChecklistEntry[]>(getGetDailyChecklistQueryKey());
    queryClient.setQueryData<DailyChecklistEntry[]>(getGetDailyChecklistQueryKey(), (old) =>
      old?.map((entry) => ({
        ...entry,
        items: entry.items.map((it) => (it.id === item.id ? { ...it, ...patch } : it)),
      })),
    );
    return previous;
  }

  function refresh() {
    queryClient.invalidateQueries({ queryKey: getGetDailyChecklistQueryKey() });
  }

  /** Fires the actual POST .../done with an optional proof payload. Shared by
   * the proof-free one-tap path and the proof dialog's submit — both patch
   * optimistically and roll back identically on failure. Returns whether the
   * tick succeeded so each caller can decide what to do next (the proof
   * dialog only closes itself on success — see handleProofSubmit below). */
  async function performTick(proof: ProofPayload): Promise<boolean> {
    const previous = patchItem({ doneByUserId: user?.id ?? null, doneAt: new Date().toISOString() });
    try {
      await doneMutation.mutateAsync({ itemId: item.id, data: proof });
      return true;
    } catch (err) {
      queryClient.setQueryData(getGetDailyChecklistQueryKey(), previous);
      // Phase 42 Plan 14 — the 422 proof_required backstop (42-09's
      // ProofPayloadWithType superRefine). Should be unreachable since the
      // dialog's submit button stays disabled until proof is attached, but
      // this is the recovery path for a stale client whose item spec (e.g.
      // proofType) changed under it. errMessage surfaces the server's own
      // message ("proof_required" etc.) verbatim via sonner.
      toast.error(errMessage(err, "Could not mark this item done."));
      return false;
    } finally {
      refresh();
    }
  }

  async function handleDoneChange(checked: boolean | "indeterminate") {
    if (checked !== true || !canAct || isSettled || doneMutation.isPending) return;
    if (requiresProof) {
      // Proof required — open the capture dialog instead of ticking
      // immediately; the tick fires on the dialog's own submit (see
      // handleProofSubmit). The UI asks before the server refuses.
      setProofOpen(true);
      return;
    }
    // proofType none/null — today's one-tap behavior, byte-for-byte
    // unchanged (Phase 43/49 rounds must not regress).
    await performTick({});
  }

  /** ProofCaptureDialog's onSubmit. On success, close the dialog (the tick is
   * done). On failure (the 422 backstop, or a network error), performTick
   * already toasted — leave `proofOpen` true so the dialog reopens/stays
   * open with every already-uploaded photo/note/signature still in its local
   * state, so retry is one tap, never re-shooting photos. */
  async function handleProofSubmit(proof: ProofPayload) {
    const ok = await performTick(proof);
    if (ok) setProofOpen(false);
  }

  async function handleNaSubmit(e: React.FormEvent) {
    e.preventDefault();
    const note = naNote.trim();
    if (note.length < 10 || naMutation.isPending) return;
    try {
      await naMutation.mutateAsync({ itemId: item.id, data: { naReason: note } });
      toast.success("Item marked N/A.");
      setNaOpen(false);
      setNaNote("");
      refresh();
    } catch (err) {
      toast.error(errMessage(err, "Could not mark this item N/A (needs at least 10 characters)."));
    }
  }

  async function handleValidate() {
    if (!canValidateNow || validateMutation.isPending) return;
    const previous = patchItem({ validatedByUserId: user?.id ?? null, validatedAt: new Date().toISOString() });
    try {
      await validateMutation.mutateAsync({ itemId: item.id });
    } catch (err) {
      queryClient.setQueryData(getGetDailyChecklistQueryKey(), previous);
      toast.error(errMessage(err, "Could not validate this item."));
    } finally {
      refresh();
    }
  }

  const remaining = 10 - naNote.trim().length;

  return (
    <div className="flex items-start gap-3 rounded-md border p-3">
      <Checkbox
        checked={isDone}
        disabled={!canAct || isSettled || doneMutation.isPending}
        onCheckedChange={handleDoneChange}
        // ~24px box — combined with the row's p-3 padding this clears a
        // 44px-ish touch target on mobile without changing desktop density.
        className="mt-0.5 h-6 w-6"
        aria-label={isDone ? "Marked done" : "Mark done"}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className={cn("text-sm font-medium leading-snug", isDone && "line-through text-muted-foreground")}>{item.label}</p>
          <OccurrenceBadges item={item} />
        </div>
        <p className="text-xs text-muted-foreground">
          {item.zoneLabel}
          {/* Group-checklist UI (migration 0120) — surfaces the linked
              floor-plan seat/cabin (e.g. "Suite 03") right on the row,
              next to the zone; separate from the group HEADER above
              (ChecklistItemGroup), which groups by zoneLabel+groupLabel. */}
          {item.zoneResourceLabel ? ` · ${item.zoneResourceLabel}` : ""}
        </p>
        <ItemStatusLine item={item} users={users} usersUnavailable={usersUnavailable} />
        <RejectedNotice item={item} users={users} usersUnavailable={usersUnavailable} />
        <ProofSummary item={item} />
        {accountDeepLink && (
          <Link
            href={`/accounts/${accountDeepLink.accountId}`}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            View account <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      <AssigneeBadge item={item} users={users} usersUnavailable={usersUnavailable} />

      {canValidateNow && (
        <div className="flex shrink-0 gap-1.5">
          <Button
            type="button"
            variant="outline"
            className="h-9 px-3 text-xs"
            onClick={handleValidate}
            disabled={validateMutation.isPending}
          >
            {validateMutation.isPending ? "Validating…" : "Validate"}
          </Button>
          {/* Phase 42 Plan 15 (TPRF-04) — the validator reviews ProofSummary
              above, then either approves (Validate) or sends it back with a
              reason (Reject). Hits 42-11's /reject route. */}
          <Button
            type="button"
            variant="destructive"
            className="h-9 px-3 text-xs"
            onClick={() => setRejectOpen(true)}
          >
            Reject
          </Button>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon" disabled={!canAct || isSettled} aria-label="Item actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setNaOpen(true)}>Mark N/A…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={naOpen} onOpenChange={(open) => { setNaOpen(open); if (!open) setNaNote(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark item N/A</DialogTitle>
            <DialogDescription>
              &ldquo;{item.label}&rdquo; — explain why this doesn&apos;t apply today (at least 10 characters).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNaSubmit} className="space-y-3">
            <Textarea
              value={naNote}
              onChange={(e) => setNaNote(e.target.value)}
              placeholder="e.g. Room closed for renovation"
              rows={3}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {remaining > 0 ? `${remaining} more character${remaining === 1 ? "" : "s"} needed` : "Ready to submit."}
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNaOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={remaining > 0 || naMutation.isPending}>
                {naMutation.isPending ? "Submitting…" : "Mark N/A"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {requiresProof && (
        <ProofCaptureDialog
          open={proofOpen}
          onOpenChange={setProofOpen}
          item={item}
          onSubmit={handleProofSubmit}
          submitting={doneMutation.isPending}
        />
      )}

      <RejectDialog open={rejectOpen} onOpenChange={setRejectOpen} itemId={item.id} itemLabel={item.label} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Group-checklist UI (migration 0120) — makes a long flat round (37 items on
// "HK — Morning Clean") usable: items are grouped into collapsible sections,
// keyed on (zoneLabel, groupLabel), while every existing per-item affordance
// above (tick/N-A/proof/validate/reject/badges) stays completely unchanged —
// this is presentation only. Operator's own resolution: "make it one task
// and create sub task such as Morning - Dusting, and under it: Temple,
// Sofa, Center Table, Printer, Reception Table."
//
// ⚠ groupLabel is NULL on any round spawned before this migration, and on
// any non-SOP item — groupChecklistItems below still buckets those (by
// zoneLabel alone, groupLabel null), so they render in their own section
// rather than being dropped. Shared by BOTH the Daily/Weekly/Monthly tab
// (ChecklistCard below) and the Onboarding/Exit tab (checklist-event-tab.tsx
// imports ChecklistItemGroup, same one render path as ChecklistItemRow).
// ─────────────────────────────────────────────────────────────────────────

interface ItemGroup {
  key: string;
  zoneLabel: string;
  groupLabel: string | null;
  items: DailyChecklistItem[];
}

/** Buckets items by (zoneLabel, groupLabel) in first-appearance order — items
 * arrive pre-sorted by seq (server `orderBy asc seq`), so a Map (insertion-
 * order-preserving) naturally keeps each group's items — and the groups
 * themselves — in the SOP's own authored order (e.g. Reception's Dusting
 * group before its Floor Care group, matching seq 1-6 then 7-8…). */
export function groupChecklistItems(items: DailyChecklistItem[]): ItemGroup[] {
  const map = new Map<string, ItemGroup>();
  for (const item of items) {
    const groupLabel = item.groupLabel ?? null;
    const key = `${item.zoneLabel} ${groupLabel ?? ""}`;
    let group = map.get(key);
    if (!group) {
      group = { key, zoneLabel: item.zoneLabel, groupLabel, items: [] };
      map.set(key, group);
    }
    group.items.push(item);
  }
  return Array.from(map.values());
}

/** True when an item would render a destructive badge (OccurrenceBadges
 * above) — Rejected / Validation overdue / Overdue. Drives auto-expand: a
 * group holding a problem item opens by default so it's never hidden behind
 * a collapsed header. */
function itemHasProblem(item: DailyChecklistItem): boolean {
  const isDone = item.doneAt != null;
  const isNa = item.naReason != null;
  const isValidated = item.validatedAt != null;
  if (item.rejectReason != null && !isDone && !isNa) return true; // rejected, not yet redone
  if ((isDone || isNa) && !isValidated && item.validationBreached) return true;
  if (!isDone && !isNa && item.doerBreached) return true;
  return false;
}

/** One collapsible section — header "{zoneLabel} — {groupLabel}" (or bare
 * "{zoneLabel}" for the null-group/ungrouped bucket) + a done/total progress
 * count, e.g. "▸ Reception — Dusting  0/6". Collapsed by default; opens
 * automatically when any item inside has a problem (see itemHasProblem). */
export function ChecklistItemGroup({ group, users, usersUnavailable, accountDeepLinkFor }: {
  group: ItemGroup;
  users: User[] | undefined;
  usersUnavailable: boolean;
  accountDeepLinkFor?: (item: DailyChecklistItem) => { accountId: number } | undefined;
}) {
  const [open, setOpen] = useState(() => group.items.some(itemHasProblem));
  const total = group.items.length;
  const doneCount = group.items.filter((i) => i.doneAt != null).length;
  const headerLabel = group.groupLabel ? `${group.zoneLabel} — ${group.groupLabel}` : group.zoneLabel;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-2.5 text-left hover:bg-muted/50">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} />
          <span className="truncate">{headerLabel}</span>
        </span>
        <Badge variant="secondary" className="shrink-0 whitespace-nowrap text-[10px]">{doneCount}/{total}</Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 p-2.5 pt-0">
        {group.items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            item={item}
            users={users}
            usersUnavailable={usersUnavailable}
            accountDeepLink={accountDeepLinkFor?.(item)}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function ChecklistCard({ entry, template, users, usersUnavailable }: {
  entry: DailyChecklistEntry;
  template: { freq: string; byweekday?: string | null; nth?: number | null; byMonthDay?: number | null } | undefined;
  users: User[] | undefined;
  usersUnavailable: boolean;
}) {
  const total = entry.items.length;
  // Honest per Phase 43 convention: only literal doneAt counts as "done" —
  // an N/A'd item doesn't inflate this number. Validated is a SEPARATE
  // metric (also counts N/A'd-then-validated items, since validatedAt is
  // set regardless of the done/N/A path) so the round's true completion
  // state — done isn't the same as resolved — stays visible.
  const doneCount = entry.items.filter((i) => i.doneAt != null).length;
  const validatedCount = entry.items.filter((i) => i.validatedAt != null).length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const subtitle = resetSubtitle(template?.freq, template?.byweekday, template?.nth, template?.byMonthDay);
  // Group-checklist UI (migration 0120) — collapsible sections instead of a
  // flat list; see groupChecklistItems/ChecklistItemGroup above.
  const groups = groupChecklistItems(entry.items);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{entry.templateName}</CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </div>
          <Badge variant="secondary" className="whitespace-nowrap">{doneCount}/{total} done</Badge>
        </div>
        <Progress value={pct} className="mt-2 h-2" />
        <p className="mt-1 text-[11px] text-muted-foreground">{validatedCount}/{total} validated</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {groups.map((g) => (
          <ChecklistItemGroup key={g.key} group={g} users={users} usersUnavailable={usersUnavailable} />
        ))}
      </CardContent>
    </Card>
  );
}

const TRIGGER_META: Record<"daily" | "weekly" | "monthly", { subtitle: string; empty: string }> = {
  daily: {
    subtitle: "Today's self-spawning rounds. Tap an item to mark it done — use the ⋮ menu for N/A.",
    empty: "No daily checklists yet today — they refresh automatically each morning.",
  },
  weekly: {
    subtitle: "This week's self-spawning rounds. Tap an item to mark it done — use the ⋮ menu for N/A.",
    empty: "No weekly checklists open right now — they refresh automatically on their reset day.",
  },
  monthly: {
    subtitle: "This month's self-spawning rounds. Tap an item to mark it done — use the ⋮ menu for N/A.",
    empty: "No monthly checklists open right now — they refresh automatically on their reset day.",
  },
};

/** The Daily/Weekly/Monthly tab body (Phase 49 Plan 08) — parameterized by
 * `trigger` so all three period tabs of the tabbed Checklist page reuse this
 * ONE component + the ONE `GET /tasks/daily-checklist` feed (already
 * server-scoped to trigger IN (daily,weekly,monthly), 49-02), partitioned
 * client-side by each entry's owning template.trigger. */
export function ChecklistDailyTab({ trigger }: { trigger: "daily" | "weekly" | "monthly" }) {
  const { data: entries, isLoading } = useGetDailyChecklist();
  const { data: templates } = useListTaskTemplates();
  // Mirrors task-create.tsx: retry:false so a 403 (non-admin viewer) settles
  // into isError quickly rather than retrying a request that will never
  // succeed for this role.
  const { data: users, isError: usersUnavailable } = useListUsers({
    query: { retry: false, queryKey: getListUsersQueryKey() },
  });

  const templateById = new Map((templates ?? []).map((t) => [t.id, t]));
  const list = (entries ?? []).filter((entry) => templateById.get(entry.templateId)?.trigger === trigger);
  const meta = TRIGGER_META[trigger];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{meta.subtitle}</p>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3"><Skeleton className="h-5 w-40" /><Skeleton className="mt-2 h-4 w-56" /></CardHeader>
              <CardContent className="space-y-2">
                {[...Array(3)].map((__, j) => <Skeleton key={j} className="h-10 w-full" />)}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex h-40 flex-col items-center justify-center gap-2 text-center">
            <ListChecks className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{meta.empty}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.map((entry) => (
            <ChecklistCard
              key={entry.templateId}
              entry={entry}
              template={templateById.get(entry.templateId)}
              users={users}
              usersUnavailable={usersUnavailable}
            />
          ))}
        </div>
      )}
    </div>
  );
}
